import { MoreHorizontal, Package, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useAgentReleases,
  useCreateRelease,
  useDeleteRelease,
  useUpdateRelease,
  type AgentRelease,
  type CpuArch,
} from "@/api/queries";
import type { Schemas } from "@/api/client";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { CopyButton } from "@/components/common/CopyButton";
import { EmptyState } from "@/components/common/EmptyState";
import { Field } from "@/components/common/Field";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input, NativeSelect } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { errorText, fieldError } from "@/lib/errors";
import { formatDateTime } from "@/lib/format";

const oses: Schemas["OS"][] = ["macos", "windows", "linux"];
const arches: CpuArch[] = ["universal", "arm64", "amd64"];

/** Tracker builds: download links and updates offered to trackers. */
export function ReleasesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const releases = useAgentReleases();
  const update = useUpdateRelease();
  const remove = useDeleteRelease();
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<AgentRelease>();
  const [editing, setEditing] = useState<AgentRelease>();

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row flex-wrap items-end justify-between gap-3 pb-4">
        <div>
          <CardTitle>{t("settings.releases.title")}</CardTitle>
          <CardDescription className="max-w-2xl">{t("settings.releases.description")}</CardDescription>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus /> {t("settings.releases.add")}
        </Button>
      </CardHeader>
      {update.error && (
        <Alert variant="danger" className="mx-5 mb-4">
          {errorText(t, update.error)}
        </Alert>
      )}
      {releases.isError ? (
        <EmptyState icon={Package} title={errorText(t, releases.error)} />
      ) : !releases.data ? (
        <Skeleton className="mx-5 mb-5 h-40" />
      ) : releases.data.length === 0 ? (
        <EmptyState
          icon={Package}
          title={t("settings.releases.empty")}
          description={t("settings.releases.emptyHint")}
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>{t("settings.releases.columns.version")}</TH>
              <TH>{t("settings.releases.columns.platform")}</TH>
              <TH>{t("settings.releases.columns.file")}</TH>
              <TH>{t("settings.releases.columns.minimum")}</TH>
              <TH>{t("settings.releases.columns.published")}</TH>
              <TH className="w-10">
                <span className="sr-only">{t("common.actions")}</span>
              </TH>
            </tr>
          </THead>
          <tbody>
            {releases.data.map((r) => (
              <TR key={r.id}>
                <TD>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold tabular-nums">{r.version}</span>
                    {r.is_latest && <Badge variant="success">{t("settings.releases.latest")}</Badge>}
                  </div>
                  <div className="text-xs text-muted">{formatDateTime(r.released_at, lang)}</div>
                </TD>
                <TD className="whitespace-nowrap">
                  {t(`os.${r.os}`)} · {t(`settings.releases.arch.${r.arch}`)}
                </TD>
                <TD className="max-w-72">
                  <div className="flex min-w-0 items-center gap-1">
                    <a
                      href={r.url}
                      className="truncate text-primary hover:underline"
                      title={r.url}
                      rel="noreferrer"
                    >
                      {r.url.split("/").pop() || r.url}
                    </a>
                    <CopyButton value={r.url} label={t("settings.releases.copyUrl")} />
                  </div>
                  <div className="truncate font-mono text-[11px] text-muted" title={r.sha256}>
                    {t("settings.releases.checksum", { value: r.sha256.slice(0, 16) })}
                  </div>
                </TD>
                <TD className="text-muted tabular-nums">{r.min_supported ?? "—"}</TD>
                <TD>
                  <Switch
                    checked={r.is_published}
                    aria-label={t("settings.releases.publishFor", { version: r.version })}
                    disabled={update.isPending}
                    onCheckedChange={(checked) => update.mutate({ id: r.id, is_published: checked })}
                  />
                </TD>
                <TD>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t("settings.releases.actionsFor", { version: r.version })}
                      >
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onSelect={() => setEditing(r)}>
                        {t("settings.releases.edit")}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-danger" onSelect={() => setDeleting(r)}>
                        {t("common.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
      {adding && <ReleaseDialog onClose={() => setAdding(false)} />}
      {editing && <ReleaseDialog release={editing} onClose={() => setEditing(undefined)} />}
      {deleting && (
        <ConfirmDialog
          title={t("settings.releases.deleteTitle", { version: deleting.version })}
          description={t("settings.releases.deleteText")}
          confirmLabel={t("common.delete")}
          danger
          errorText={(e) => errorText(t, e)}
          onConfirm={() => remove.mutateAsync(deleting.id)}
          onClose={() => setDeleting(undefined)}
        />
      )}
    </Card>
  );
}

/** Adds a build or edits the notes and the minimum version of one. */
function ReleaseDialog({ release, onClose }: { release?: AgentRelease; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateRelease();
  const update = useUpdateRelease();
  const [version, setVersion] = useState("");
  const [os, setOs] = useState<Schemas["OS"]>("macos");
  const [arch, setArch] = useState<CpuArch>("universal");
  const [url, setUrl] = useState("");
  const [sha256, setSha256] = useState("");
  const [minimum, setMinimum] = useState(release?.min_supported ?? "");
  const [notes, setNotes] = useState(release?.notes ?? "");
  const error = release ? update.error : create.error;
  const pending = create.isPending || update.isPending;
  const known = ["version", "os", "arch", "url", "sha256", "min_supported"];
  const other = error && !known.some((f) => fieldError(t, error, f)) ? errorText(t, error) : null;

  const submit = async () => {
    if (release) {
      await update.mutateAsync({ id: release.id, notes, min_supported: minimum.trim() });
    } else {
      await create.mutateAsync({
        version: version.trim(),
        os,
        arch,
        url: url.trim(),
        sha256: sha256.trim(),
        notes: notes.trim() || undefined,
        min_supported: minimum.trim() || undefined,
        is_published: true,
      });
    }
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={
          release
            ? t("settings.releases.editTitle", { version: release.version })
            : t("settings.releases.addTitle")
        }
        description={release ? undefined : t("settings.releases.addHint")}
      >
        <form
          className="flex flex-col gap-4 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            void submit().catch(() => undefined);
          }}
        >
          {!release && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label={t("settings.releases.columns.version")} error={fieldError(t, error, "version")}>
                  <Input
                    value={version}
                    required
                    placeholder="1.0.0"
                    onChange={(e) => setVersion(e.target.value)}
                  />
                </Field>
                <Field label={t("settings.releases.os")} error={fieldError(t, error, "os")}>
                  <NativeSelect
                    value={os}
                    onChange={(e) => {
                      const next = e.target.value as Schemas["OS"];
                      setOs(next);
                      if (next !== "macos" && arch === "universal") setArch("amd64");
                    }}
                    className="h-10"
                  >
                    {oses.map((o) => (
                      <option key={o} value={o}>
                        {t(`os.${o}`)}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label={t("settings.releases.archLabel")} error={fieldError(t, error, "arch")}>
                  <NativeSelect
                    value={arch}
                    onChange={(e) => setArch(e.target.value as CpuArch)}
                    className="h-10"
                  >
                    {arches
                      .filter((a) => os === "macos" || a !== "universal")
                      .map((a) => (
                        <option key={a} value={a}>
                          {t(`settings.releases.arch.${a}`)}
                        </option>
                      ))}
                  </NativeSelect>
                </Field>
              </div>
              <Field
                label={t("settings.releases.url")}
                hint={t("settings.releases.urlHint")}
                error={fieldError(t, error, "url")}
              >
                <Input
                  type="url"
                  value={url}
                  required
                  placeholder="https://"
                  onChange={(e) => setUrl(e.target.value)}
                />
              </Field>
              <Field
                label={t("settings.releases.sha")}
                hint={t("settings.releases.shaHint")}
                error={fieldError(t, error, "sha256")}
              >
                <Input
                  value={sha256}
                  required
                  spellCheck={false}
                  className="font-mono text-xs"
                  onChange={(e) => setSha256(e.target.value)}
                />
              </Field>
            </>
          )}
          <Field
            label={t("settings.releases.columns.minimum")}
            hint={t("settings.releases.minimumHint")}
            error={fieldError(t, error, "min_supported")}
          >
            <Input value={minimum} placeholder="1.0.0" onChange={(e) => setMinimum(e.target.value)} />
          </Field>
          <Field label={t("settings.releases.notes")}>
            <textarea
              value={notes}
              maxLength={4000}
              rows={3}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-lg border bg-surface px-3 py-2 shadow-card"
            />
          </Field>
          {other && <Alert variant="danger">{other}</Alert>}
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={pending}>
              {release ? t("common.save") : t("settings.releases.add")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
